import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Typography,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useConversations } from '../hooks/useConversations';
import { useConversationsContext } from '../contexts/ConversationsContext';
import { getWebSocketManager } from '../utils/websocket';
import { formatConversationDate } from '../utils/dateFormatter';
import SettingsModal from './settings/SettingsModal';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onNewChat: () => void;
  onSelectConversation?: (conversationId: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  onNewChat,
  onSelectConversation,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Get conversations from context
  const { conversations, loading, error } = useConversations();
  const { updateConversationTitle } = useConversationsContext();

  // Set up WebSocket title updates
  useEffect(() => {
    const wsManager = getWebSocketManager();
    
    const unsubscribe = wsManager.onTitleUpdate(({ conversationId, title }) => {
      console.log('Sidebar received title update for conversation:', conversationId, 'with title:', title);
      updateConversationTitle(conversationId, title);
      console.log('Called updateConversationTitle in context');
    });

    return unsubscribe;
  }, [updateConversationTitle]);

  const drawerWidth = 280;

  const sidebarContent = (
    <Box
      sx={{
        width: drawerWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header with New Chat button */}
      <Box sx={{ p: 2 }}>
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<AddIcon />}
          onClick={() => {
            onNewChat();
            if (isMobile) onClose();
          }}
          sx={{
            borderRadius: 2,
            py: 1.5,
            borderColor: 'divider',
            color: 'text.primary',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
            },
          }}
        >
          New conversation
        </Button>
      </Box>

      <Divider />

      {/* Previous Chats */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              color="error"
              sx={{ fontSize: '0.875rem' }}
            >
              Failed to load conversations
            </Typography>
          </Box>
        ) : conversations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              color="text.disabled"
              sx={{ 
                fontStyle: 'italic',
                fontSize: '0.875rem'
              }}
            >
              No conversations yet
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 1 }}>
            {conversations.map((conversation) => (
              <ListItemButton
                key={conversation.conversationId}
                onClick={() => {
                  if (onSelectConversation) {
                    onSelectConversation(conversation.conversationId);
                  }
                  if (isMobile) onClose();
                }}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  mx: 1,
                  '&:hover': {
                    bgcolor: 'primary.50',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ChatIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {conversation.title || 'New Conversation'}
                    </Typography>
                  }
                  secondary={
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {formatConversationDate(conversation.lastActivity)}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </Box>
        )}
      </Box>

      <Divider />

      {/* Profile Section */}
      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={() => {
            setSettingsOpen(true);
            if (isMobile) onClose();
          }}
          sx={{
            borderRadius: 2,
            '&:hover': {
              bgcolor: 'primary.50',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              <PersonIcon fontSize="small" />
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Profile & Settings
              </Typography>
            }
          />
        </ListItemButton>
      </Box>

      {/* Settings Modal */}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            top: '64px', // Start below the AppBar
            height: 'calc(100vh - 64px)',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: drawerWidth,
        height: 'calc(100vh - 64px)', // Account for AppBar height
        position: 'fixed',
        left: 0,
        top: '64px', // Start below the AppBar
        borderRight: 1,
        borderColor: 'divider',
        zIndex: theme.zIndex.drawer,
      }}
    >
      {sidebarContent}
    </Paper>
  );
};

export default Sidebar;
